import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  useApi,
  ApiError,
  type ClosedWhanau,
  type ClosedWhanauListResponse,
} from "../api/client";
import { formatDate } from "../api/formatDate";
import "./WhanauDirectory.css";
import "./ClosedDirectoryView.css";

/**
 * Closed whānau directory at /whanau/closed.
 *
 * Lighter sibling of WhanauDirectory:
 *   - No search/sort controls — closed lists are read-mostly references
 *   - Sorted by exit date (most recently exited first)
 *   - Shows the closed_reason chip ('Declined' or 'Exit completed')
 *   - Read-only navigation back into the whānau profile is still allowed
 *     (kaimahi can review notes / past sessions, the wizard's submission
 *     rules block new sessions on a closed whānau because there's no
 *     subsequent cycle expected)
 *
 * Service-membership-scoped server-side via /api/v1/whanau/closed.
 */
export function ClosedDirectoryView() {
  const { t } = useTranslation();
  const callApi = useApi();

  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; data: ClosedWhanauListResponse }
    | { kind: "error"; message: string; status?: number }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await callApi<ClosedWhanauListResponse>("/whanau/closed");
        if (cancelled) return;
        setState({ kind: "ok", data });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setState({
            kind: "error",
            status: err.status,
            message: err.message,
          });
        } else {
          const message = err instanceof Error ? err.message : "Unknown error";
          setState({ kind: "error", message });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callApi]);

  return (
    <section className="closed-directory">
      <header className="closed-directory__header">
        <div>
          <h1 className="closed-directory__title">
            {t("closedDirectory.title")}
          </h1>
          <p className="closed-directory__subtitle">
            {t("closedDirectory.subtitle")}
          </p>
        </div>
        <Link to="/directory" className="closed-directory__back">
          {t("closedDirectory.backToDirectory")}
        </Link>
      </header>

      {state.kind === "loading" && (
        <p className="closed-directory__status">
          {t("closedDirectory.loading")}
        </p>
      )}

      {state.kind === "error" && (
        <p className="closed-directory__status closed-directory__status--error">
          {state.status
            ? `${String(state.status)} — ${state.message}`
            : state.message}
        </p>
      )}

      {state.kind === "ok" && state.data.rows.length === 0 && (
        <p className="closed-directory__status">{t("closedDirectory.empty")}</p>
      )}

      {state.kind === "ok" && state.data.rows.length > 0 && (
        <ClosedList rows={state.data.rows} />
      )}
    </section>
  );
}

function ClosedList({ rows }: { rows: ClosedWhanau[] }) {
  const { t } = useTranslation();
  return (
    <ul className="closed-directory__list">
      {rows.map((row) => (
        <li key={row.whanau_id} className="closed-directory__row">
          <Link
            to={`/whanau/${String(row.whanau_id)}`}
            className="closed-directory__row-link"
          >
            <span className="closed-directory__name">{row.display_name}</span>
            <span className="closed-directory__meta">
              <span
                className={`closed-directory__reason closed-directory__reason--${row.closed_reason}`}
              >
                {row.closed_reason === "declined"
                  ? t("closedDirectory.reasonDeclined")
                  : t("closedDirectory.reasonExitCompleted")}
              </span>
              {row.exited_at && (
                <span className="closed-directory__exited">
                  {t("closedDirectory.exitedOn", {
                    date: formatDate(row.exited_at),
                  })}
                </span>
              )}
              {row.primary_location && (
                <span className="closed-directory__location">
                  {row.primary_location}
                </span>
              )}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export default ClosedDirectoryView;
